import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./hooks/useAuth";

/**
 * Patches Radix UI toast focus-guard sentinel spans to remove them from the
 * tab order. Radix renders <span aria-hidden="true" tabindex="0"> as focus
 * guards, which Lighthouse flags as an accessibility violation
 * (aria-hidden-focus). Setting tabindex="-1" satisfies the audit while
 * preserving Radix's focus management for the toast region itself.
 */
function useFixAriaHiddenFocusGuards() {
  useEffect(() => {
    const fix = () => {
      document
        .querySelectorAll<HTMLSpanElement>('span[aria-hidden="true"][tabindex="0"]')
        .forEach((el) => {
          el.setAttribute("tabindex", "-1");
        });
    };
    fix();
    const observer = new MutationObserver(fix);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}
import Index from "./pages/Index";
import EnhancedIndex from "./pages/EnhancedIndex";
import Auth from "./pages/Auth";
import Tipsters from "./pages/Tipsters";
import Tips from "./pages/Tips";
import CreateTip from "./pages/CreateTip";
import BecomeTipster from "./pages/BecomeTipster";
import LiveGames from "./pages/LiveGames";
import InteractiveTips from "./pages/InteractiveTips";
import PredictionDashboard from "./pages/PredictionDashboard";
import Leaderboard from "./pages/Leaderboard";
import UserDashboard from "./pages/UserDashboard";
import Blog from "./pages/Blog";
import Engagement from "./pages/Engagement";
import Esports from "./pages/Esports";
import SafeGaming from "./pages/SafeGaming";
import Analytics from "./pages/Analytics";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => {
  useFixAriaHiddenFocusGuards();
  return (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <main id="main-content">
          <Routes>
            <Route path="/" element={<EnhancedIndex />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/tipsters" element={<Tipsters />} />
            <Route path="/tips" element={<Tips />} />
            <Route path="/live" element={<LiveGames />} />
            <Route path="/create-tip" element={<CreateTip />} />
            <Route path="/become-tipster" element={<BecomeTipster />} />
            <Route path="/interactive-tips" element={<InteractiveTips />} />
            <Route path="/prediction-dashboard" element={<PredictionDashboard />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/engagement" element={<Engagement />} />
            <Route path="/esports" element={<Esports />} />
            <Route path="/safe-gaming" element={<SafeGaming />} />
            <Route path="/analytics" element={<Analytics />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </main>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  );
};

export default App;
